import { Type, type Static, type TOptional, type TString } from '@sinclair/typebox';
import {
  SeoArticleStatus,
  SeoBacklinkOrigin,
  SeoBacklinkStatus,
  SeoBacklinkTargetFamily,
  SeoKeywordStatus,
} from './translation_namespace/seo_content_namespace';

/**
 * Admin « Référencement » (linkuma-integration.md §8, seo-content-pipeline-plan.md §5.7) :
 *  - Backlinks : lignes du mois par pro (origine, famille, ancre, page cible, hôte publié, coût,
 *    vérification), vue globale (commandés / publiés / en écart, coût contre budget, solde,
 *    répartition des familles et des ancres) ;
 *  - Articles : par pro et par mois, sujets proposés / validés / refusés et articles par état
 *    (en rédaction, QA en échec, à relire, publiés), liste des articles.
 * Les montants (centimes HT en base) sont formatés par le backend, comme partout (`is_price`).
 */

const formatted = (sourceKey: string, withTime = false): TOptional<TString> =>
  Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: sourceKey, is_short: true, with_time: withTime } }),
  );

export const AdminSeoBacklinkItemSchema = Type.Object({
  seo_backlink_id: Type.Integer(),
  professional_id: Type.Integer(),
  professional_name: Type.String(),
  month: Type.String(),
  pack_month: Type.Optional(Type.String()),
  origin: Type.Optional(Type.Enum(SeoBacklinkOrigin)),
  status: Type.Enum(SeoBacklinkStatus),
  writing_mode: Type.Optional(Type.String()),
  target_family: Type.Optional(Type.Enum(SeoBacklinkTargetFamily)),
  target_url: Type.Optional(Type.String()),
  target_replaced: Type.Boolean(),
  anchor: Type.Optional(Type.String()),
  anchor_type: Type.Optional(Type.String()),
  page_keyword: Type.Optional(Type.String()),
  source_url: Type.Optional(Type.String()),
  source_host: Type.Optional(Type.String()),
  /** Prix HT payé au fournisseur, formaté par le backend (`cost_ht_minor` en base). */
  cost_ht: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'cost_ht_minor' } })),
  provider_order_id: Type.Optional(Type.String()),
  thematic_id: Type.Optional(Type.String()),
  /** Retrouvé dans l'index d'un moteur. */
  indexed: Type.Boolean(),
  ordered_at_formatted: formatted('ordered_at', true),
  scheduled_for_formatted: formatted('scheduled_for'),
  published_at_formatted: formatted('published_at'),
  verified_at_formatted: formatted('verified_at', true),
  verify_failure: Type.Optional(Type.String()),
  failure_reason: Type.Optional(Type.String()),
  review_due_at_formatted: formatted('review_due_at'),
  reviewed_at_formatted: formatted('reviewed_at', true),
  /** Brief envoyé au rédacteur (audit, support). */
  brief: Type.Optional(Type.String()),
});
export type AdminSeoBacklinkItemObject = Static<typeof AdminSeoBacklinkItemSchema>;

export const AdminSeoBacklinkListResponseSchema = Type.Object({
  items: Type.Array(AdminSeoBacklinkItemSchema),
  total: Type.Integer(),
  page: Type.Integer(),
  per_page: Type.Integer(),
});
export type AdminSeoBacklinkListResponseObject = Static<typeof AdminSeoBacklinkListResponseSchema>;

export const AdminSeoCountSchema = Type.Object({ key: Type.String(), count: Type.Integer() });
export type AdminSeoCountObject = Static<typeof AdminSeoCountSchema>;

export const AdminSeoBacklinkSummarySchema = Type.Object({
  month: Type.String(),
  month_label: Type.String(),
  /** Pros au pack actif (site publié ou non). */
  pros_active: Type.Integer(),
  /** Pros au pack actif sans aucune ligne ce mois-ci (rien commandé, rien à relire). */
  pros_without_link: Type.Integer(),
  to_review: Type.Integer(),
  expired: Type.Integer(),
  ordered: Type.Integer(),
  in_progress: Type.Integer(),
  published: Type.Integer(),
  mismatch: Type.Integer(),
  replaced: Type.Integer(),
  failed: Type.Integer(),
  /** Dépense HT du mois (commandes acceptées), formatée par le backend. */
  cost_ht: Type.String({ meta: { is_price: true, source_key: 'cost_ht_minor' } }),
  /** Plafond `LINKUMA_MONTHLY_BUDGET_EUR` formaté ; absent = pas de plafond. */
  budget: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'budget_minor' } })),
  /** Solde de crédits chez le fournisseur, formaté ; absent si le jeton manque ou si l'appel échoue. */
  credit: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'credit_minor' } })),
  by_family: Type.Array(AdminSeoCountSchema),
  by_anchor_type: Type.Array(AdminSeoCountSchema),
  by_origin: Type.Array(AdminSeoCountSchema),
});
export type AdminSeoBacklinkSummaryObject = Static<typeof AdminSeoBacklinkSummarySchema>;

/** Un pro, un mois : où en sont ses sujets et ses articles. */
export const AdminSeoArticleProRowSchema = Type.Object({
  professional_id: Type.Integer(),
  professional_name: Type.String(),
  website_published: Type.Boolean(),
  /** Pack ouvert à la main (`seo_articles_enabled_at`) ou à tous. */
  pack_open: Type.Boolean(),
  /** Parcours de première activation suivi : avant, le cycle ne produit rien pour ce pro. */
  onboarded: Type.Boolean(),
  backlink_mode: Type.Optional(Type.String()),
  topics_proposed: Type.Integer(),
  topics_approved: Type.Integer(),
  topics_rejected: Type.Integer(),
  topics_covered: Type.Integer(),
  articles_generating: Type.Integer(),
  articles_qa_failed: Type.Integer(),
  articles_ready: Type.Integer(),
  articles_published: Type.Integer(),
  articles_rejected: Type.Integer(),
  /** Dernière notification « sujets à valider » (proposition ou relance). */
  topics_notified_at_formatted: formatted('topics_notified_at'),
});
export type AdminSeoArticleProRowObject = Static<typeof AdminSeoArticleProRowSchema>;

export const AdminSeoArticlesOverviewResponseSchema = Type.Object({
  month: Type.String(),
  month_label: Type.String(),
  items: Type.Array(AdminSeoArticleProRowSchema),
  /** Totaux de la colonne, mêmes clés que les lignes. */
  totals: Type.Object({
    topics_proposed: Type.Integer(),
    topics_approved: Type.Integer(),
    topics_rejected: Type.Integer(),
    articles_generating: Type.Integer(),
    articles_qa_failed: Type.Integer(),
    articles_ready: Type.Integer(),
    articles_published: Type.Integer(),
    articles_rejected: Type.Integer(),
  }),
});
export type AdminSeoArticlesOverviewResponseObject = Static<typeof AdminSeoArticlesOverviewResponseSchema>;

export const AdminSeoArticleItemSchema = Type.Object({
  seo_article_id: Type.Integer(),
  professional_id: Type.Optional(Type.Integer()),
  professional_name: Type.Optional(Type.String()),
  title: Type.String(),
  status: Type.Enum(SeoArticleStatus),
  keyword: Type.String(),
  keyword_status: Type.Enum(SeoKeywordStatus),
  target_month: Type.Optional(Type.String()),
  score: Type.Optional(Type.Integer()),
  word_count: Type.Integer(),
  qa_tips: Type.Array(Type.String()),
  published_url: Type.Optional(Type.String()),
  created_at_formatted: formatted('created_at', true),
  published_at_formatted: formatted('published_at'),
  notified_at_formatted: formatted('notified_at', true),
});
export type AdminSeoArticleItemObject = Static<typeof AdminSeoArticleItemSchema>;

export const AdminSeoArticleListResponseSchema = Type.Object({
  items: Type.Array(AdminSeoArticleItemSchema),
  total: Type.Integer(),
  page: Type.Integer(),
  per_page: Type.Integer(),
});
export type AdminSeoArticleListResponseObject = Static<typeof AdminSeoArticleListResponseSchema>;

export const AdminSeoActionResponseSchema = Type.Object({
  ok: Type.Boolean(),
  /** Détail lisible pour l'admin (ex. « 2 liens commandés, 1 en échec »). */
  detail: Type.Optional(Type.String()),
});
export type AdminSeoActionResponseObject = Static<typeof AdminSeoActionResponseSchema>;
